import { Flex, Typography } from 'antd';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DateTime from 'utils/datetime';

type RemainingTime = {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  isNegative?: boolean;
};

interface TimerProps {
  toTime: Date;
  updateEvery: number;
  initialTime?: RemainingTime;
  containerClasses?: string;
  textClasses?: string;
  renderer?: (time: RemainingTime) => React.ReactNode;
  showDays?: boolean;
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
  countBelowZero?: boolean;
  enabled?: boolean;
  onTimerEnd?: () => void;
  'data-testid'?: string;
}

const initialRemainingTime = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

const Timer = ({
  initialTime,
  containerClasses,
  textClasses,
  toTime,
  renderer,
  updateEvery = 1000,
  showDays = true,
  showHours = true,
  showMinutes = true,
  showSeconds = true,
  countBelowZero = false,
  enabled = true,
  onTimerEnd,
  'data-testid': dataTestId,
}: TimerProps) => {
  const [remainingTime, setRemainingTime] = useState<RemainingTime>(
    initialTime ?? initialRemainingTime,
  );
  const timerEndFnCallCountRef = useRef(0);

  const classes = {
    container: `timer-container ${containerClasses || ''}`,
    text: `timer-text ${textClasses || ''}`,
  };

  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      const now = new Date();
      const isBeforeEndTime = DateTime.isBefore(now, toTime);

      if (!isBeforeEndTime) {
        if (timerEndFnCallCountRef.current === 0) onTimerEnd?.();
        timerEndFnCallCountRef.current += 1;

        if (!countBelowZero) {
          setRemainingTime(initialRemainingTime);
          clearInterval(interval);
          return;
        }
      }
      const timeRemaining = DateTime.calculateTimeRemaining(now, toTime);
      setRemainingTime(timeRemaining);
    }, updateEvery);

    return () => clearInterval(interval);
  }, [countBelowZero, enabled, onTimerEnd, toTime, updateEvery]);

  const prependZero = useCallback(
    (value: number) => (value < 10 ? `0${value}` : value),
    [],
  );

  const adjustTimerValues = useCallback(() => {
    let hours = remainingTime?.hours;
    let minutes = remainingTime?.minutes;
    let seconds = remainingTime?.seconds;

    if (!showDays && remainingTime?.days)
      hours = hours! + remainingTime?.days * 24;

    if (!showHours) minutes = minutes! + hours! * 60;

    if (!showMinutes) seconds = seconds! + minutes! * 60;

    return { hours, minutes, seconds };
  }, [
    remainingTime?.days,
    remainingTime?.hours,
    remainingTime?.minutes,
    remainingTime?.seconds,
    showDays,
    showHours,
    showMinutes,
  ]);

  const time = useMemo(() => adjustTimerValues(), [adjustTimerValues]);
  const { hours, minutes, seconds } = time;
  const days = remainingTime?.days || 0;

  const renderSeperator = (label: string) => {
    const separatorDisplay = {
      days: showHours || showMinutes || showSeconds,
      hours: showMinutes || showSeconds,
      minutes: showSeconds,
      seconds: false,
    };

    return separatorDisplay[label as keyof typeof separatorDisplay] ? ':' : '';
  };

  return (
    <Flex align="center" gap={5} className={classes.container}>
      {renderer && renderer(remainingTime)}
      {!renderer && (
        <Typography.Text
          color={'#000'}
          className={classes.text}
          data-testid={dataTestId}
        >
          {remainingTime?.isNegative ? '-' : ''}
          {showDays && `${prependZero(days)}${renderSeperator('days')}`}
          {showHours && `${prependZero(hours!)}${renderSeperator('hours')}`}
          {showMinutes &&
            `${prependZero(minutes!)}${renderSeperator('minutes')}`}
          {showSeconds && `${prependZero(seconds!)}`}
        </Typography.Text>
      )}
    </Flex>
  );
};

export default memo(Timer);
